/**
 * @file utils/helpers.js
 * @description Shared utility functions used across the app.
 */

/**
 * Format a Firestore Timestamp or JS Date into a readable string.
 * @param {import("firebase/firestore").Timestamp | Date | null} ts
 * @returns {string}  e.g. "Mon, 24 Mar 2026 · 7:42 PM"
 */
export function formatDate(ts) {
  if (!ts) return "—";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Format a Firestore Timestamp into a short date string for grouping.
 * @param {import("firebase/firestore").Timestamp | Date | null} ts
 * @returns {string}  e.g. "24 Mar 2026"
 */
export function formatDateShort(ts) {
  if (!ts) return "—";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * Returns a colour hex string based on calorie percentage of daily target.
 * @param {number} cal  – calories consumed
 * @param {number} target – daily calorie target
 * @returns {string}
 */
export function calorieColor(cal, target) {
  const pct = cal / target;
  if (pct < 0.3) return "#3B82F6"; // blue  – very light
  if (pct < 0.6) return "#10B981"; // green – on track
  if (pct < 0.95) return "#F59E0B"; // amber – getting heavy
  return "#EF4444"; // red   – over or close to limit
}

/**
 * Clamp a number between min and max.
 * @param {number} val
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

/**
 * Group an array of meal objects by their short date string.
 * @param {Array<{ createdAt: any }>} meals
 * @returns {Record<string, Array>}
 */
export function groupByDate(meals) {
  return meals.reduce((acc, meal) => {
    const key = formatDateShort(meal.createdAt);
    if (!acc[key]) acc[key] = [];
    acc[key].push(meal);
    return acc;
  }, {});
}

export function sortGroupedByDate(groupedMeals) {
  const sorted = {};
  const dateKeys = Object.keys(groupedMeals).sort((a, b) => {
    // "25 Mar 2026" parses correctly with new Date()
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateB - dateA; // Newest first (descending)
  });

  dateKeys.forEach((key) => {
    sorted[key] = groupedMeals[key];
  });

  return sorted;
}
